import { MedalDef, MedalLayers, dirtOfLayers, layersOfDirt } from "../shared/achievements";
import { pushLayer } from "./history_stack";
import { t } from "./i18n";
import { BIG_CANVAS, calm, dullOf, medalEl } from "./medal_art";

// the workbench: one medal under a lamp on a baize cloth, a foam board of three tools, a brass tag that says how it stands.
// three layers cover a medal and each tool removes exactly one: the brush the rust, the sponge the grime beneath it,
// the cloth the haze on the metal. a tool only bites once the layers above its own are gone, and nothing swaps by itself

type ToolId = "brush" | "sponge" | "cloth";
type LayerId = keyof MedalLayers;

interface Tool {
  layer: LayerId;
  // stroke width on a 270 wide medal and how much of the layer one pass takes
  w: number;
  p: number;
}

const TOOLS: Record<ToolId, Tool> = {
  brush: { layer: "rust", w: 26, p: 0.6 },
  sponge: { layer: "grime", w: 58, p: 0.5 },
  cloth: { layer: "haze", w: 62, p: 0.45 }
};
const TOOL_IDS = Object.keys(TOOLS) as ToolId[];
const LAYERS: LayerId[] = ["rust", "grime", "haze"];
const DONE = 0.08;
const W = BIG_CANVAS;

const TOOLSVG: Record<ToolId, string> = {
  brush: '<svg viewBox="0 0 120 120" aria-hidden="true"><g transform="rotate(-35 60 60)"><rect x="52" y="8" width="16" height="58" rx="8" fill="#8a5a2e"/><rect x="50" y="62" width="20" height="16" fill="#c79a3a"/><rect x="48" y="78" width="24" height="30" rx="3" fill="#d9c28a"/><path d="M54 80V106M60 80V108M66 80V106" stroke="#a8915a" stroke-width="2"/></g></svg>',
  sponge: '<svg viewBox="0 0 120 120" aria-hidden="true"><rect x="18" y="34" width="84" height="56" rx="16" fill="#f2d24a" stroke="#c9a826" stroke-width="2"/><rect x="18" y="72" width="84" height="18" rx="9" fill="#4da36b"/><circle cx="38" cy="50" r="4" fill="#d9b52e"/><circle cx="62" cy="44" r="3" fill="#d9b52e"/><circle cx="82" cy="56" r="4.5" fill="#d9b52e"/><circle cx="52" cy="62" r="3.5" fill="#d9b52e"/><circle cx="90" cy="42" r="2.5" fill="#d9b52e"/><circle cx="104" cy="28" r="5" fill="none" stroke="#bfe9ff" stroke-width="2"/><circle cx="96" cy="18" r="3" fill="none" stroke="#bfe9ff" stroke-width="2"/></svg>',
  cloth: '<svg viewBox="0 0 120 120" aria-hidden="true"><path d="M14 38L96 26L108 78L30 98Z" fill="url(#medal-weave)" stroke="#a89a78" stroke-width="2"/><path d="M30 98L24 58M60 92L52 46" stroke="#bfae86" stroke-width="2" fill="none"/><path d="M14 38L96 26" stroke="#5b7fa8" stroke-width="5"/></svg>'
};

const toolName = (id: ToolId): string => t(id);
const toolHint = (id: ToolId): string => (id === "brush" ? t("scrape off the rust") : id === "sponge" ? t("scrub off the grime") : t("polish it to a shine"));

interface Particle {
  kind: string;
  x: number;
  y: number;
  x2?: number;
  y2?: number;
  vx: number;
  vy: number;
  g: number;
  life: number;
  c: string;
  s: number;
}

// what a medal says about itself: which achievement earned it, what that takes and when
export interface Placard {
  // "Gold · Studio shelf"
  meta: string;
  by: string;
  desc: string;
  // empty while the medal is not earned yet
  date: string;
}

// the brass plate that goes under a medal, on the workbench and on the card for a medal you do not have yet
export function placardEl(def: MedalDef, info: Placard): HTMLElement {
  const plate = document.createElement("div");
  plate.className = "mb-placard";
  const title = document.createElement("h3");
  title.textContent = t(def.name);
  const meta = document.createElement("small");
  meta.textContent = info.meta;
  title.append(meta);
  const list = document.createElement("dl");
  const row = (label: string, ...cells: Array<[string, string?]>): void => {
    const dt = document.createElement("dt");
    dt.textContent = label;
    const dd = document.createElement("dd");
    for (const [text, tag] of cells) {
      const part = document.createElement(tag || "span");
      part.textContent = text;
      dd.append(part);
    }
    list.append(dt, dd);
  };
  row(info.date ? t("Earned for") : t("How to earn it"), [info.by, "b"], [info.desc]);
  row(t("Date"), [info.date || t("Not earned yet")]);
  list.lastElementChild?.classList.add("when");
  plate.append(title, list);
  return plate;
}

export function openInfo(def: MedalDef, info: Placard, from?: HTMLElement | null): void {
  const sheet = document.createElement("div");
  sheet.className = "mb-sheet mb-info";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-label", t(def.name));
  const card = document.createElement("div");
  card.className = "mb-bench";
  const shadow = medalEl(def, { size: 150, locked: true });
  shadow.classList.add("mb-info-medal");
  shadow.setAttribute("aria-hidden", "true");
  const close = document.createElement("button");
  close.type = "button";
  close.className = "m-brass mb-putback";
  close.textContent = t("Close");
  card.append(shadow, placardEl(def, info), close);
  sheet.append(card);
  let done = false;
  const hide = (): void => {
    if (done) return;
    done = true;
    document.removeEventListener("keydown", onKey);
    sheet.remove();
    from?.focus?.();
  };
  const release = pushLayer(hide);
  const shut = (): void => {
    if (done) return;
    hide();
    release();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") shut();
  };
  document.addEventListener("keydown", onKey);
  close.addEventListener("click", shut);
  sheet.addEventListener("click", (e) => {
    if (e.target === sheet) shut();
  });
  document.body.append(sheet);
  close.focus({ preventScroll: true });
}

export interface BenchOptions {
  def: MedalDef;
  placard?: Placard;
  // the medal's dirt now, 0 spotless to 1 filthy
  dirt: number;
  // called, after a rubbing session ends, with the lower dirt to keep
  onSave: (dirt: number) => void;
  // called when the sheet closes, by the button, escape or Back
  onClose: () => void;
  returnFocus?: HTMLElement | null;
}

export interface BenchHandle {
  close: () => void;
  root: HTMLElement;
}

export function openBench(opts: BenchOptions): BenchHandle {
  const { def } = opts;
  const startLayers = layersOfDirt(opts.dirt);
  const polishedAtStart = opts.dirt <= 0.02;
  const sheet = document.createElement("div");
  sheet.className = "mb-sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-label", t(def.name));
  sheet.innerHTML = `<svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs><pattern id="medal-weave" width="6" height="6" patternUnits="userSpaceOnUse"><rect width="6" height="6" fill="#e9e0c8"/><path d="M0 3H6M3 0V6" stroke="#cdbf9c" stroke-width="1"/></pattern></defs></svg>
    <div class="mb-bench">
      <div class="mb-baize"><div class="mb-lamp"></div><div class="mb-pad"></div><div class="mb-tilt"></div><div class="mb-guide" aria-hidden="true"></div>
        <div class="mb-tag"><div class="mb-card"><span class="mb-cond"></span><b class="mb-word"></b><span class="mb-pct"></span><em class="mb-hint" aria-live="polite"></em></div></div></div>
      <div class="mb-lip"><span class="m-brass mb-name"></span><button type="button" class="m-brass mb-putback"></button></div>
      <div class="mb-placard-slot"></div>
      <div class="mb-foam" role="group"></div>
    </div>
    <div class="mb-held" aria-hidden="true"></div>`;
  const q = <T extends HTMLElement>(sel: string): T => sheet.querySelector<T>(sel)!;
  const baize = q(".mb-baize"), tilt = q(".mb-tilt"), guide = q(".mb-guide"), tag = q(".mb-tag"), foam = q(".mb-foam"), held = q(".mb-held"), hintEl = q(".mb-hint");
  q(".mb-cond").textContent = t("condition");
  q(".mb-name").textContent = t(def.name);
  if (opts.placard) q(".mb-placard-slot").replaceWith(placardEl(def, opts.placard));
  const putBack = q<HTMLButtonElement>(".mb-putback");
  putBack.textContent = t("Put back in the case");

  const medal = medalEl(def, { size: 270, big: true, layers: startLayers });
  medal.style.setProperty("--s", "min(270px, 72vw)");
  tilt.append(medal);
  const rust = medal.querySelector<HTMLCanvasElement>(".m-rust")!;
  const ctxs = {} as Record<LayerId, CanvasRenderingContext2D>;
  for (const k of LAYERS) ctxs[k] = medal.querySelector<HTMLCanvasElement>(`.m-${k}`)!.getContext("2d", { willReadFrequently: true })!;
  const fctx = medal.querySelector<HTMLCanvasElement>(".m-fx")!.getContext("2d")!;
  rust.tabIndex = 0;
  rust.setAttribute("role", "button");
  rust.style.touchAction = "none";

  const sample = (ctx: CanvasRenderingContext2D): number => {
    const d = ctx.getImageData(0, 0, W, W).data;
    let n = 0;
    for (let y = 6; y < W; y += 12) for (let x = 6; x < W; x += 12) if (d[(y * W + x) * 4 + 3] > 36) n++;
    return n;
  };
  const base = { rust: sample(ctxs.rust), grime: sample(ctxs.grime), haze: sample(ctxs.haze) };
  const left: MedalLayers = { ...startLayers };
  let clean = 0;
  let holding: ToolId = "brush";
  let rubbing = false;
  let over = false;
  let last = { x: 0, y: 0 };
  let parts: Particle[] = [];
  let loop = 0;
  let vibeAt = 0;
  let nopeAt = -1e9;
  let sayTimer = 0;
  let lastWord = "";
  let keyRow = 0;
  let saveTimer = 0;
  let lastSaved = opts.dirt;
  let closed = false;
  let lastHX = 0;
  const timers = new Set<number>();
  const later = (fn: () => void, ms: number): void => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
  };

  const done = (): boolean => left.rust < DONE && left.grime < DONE && left.haze < DONE;
  const needed = (): ToolId => (left.rust > DONE ? "brush" : left.grime > DONE ? "sponge" : "cloth");
  const word = (): string => (left.rust > 0.12 ? "Rusty" : left.grime > 0.12 ? "Tarnished" : left.haze > 0.3 ? "Dull" : left.haze > 0.08 ? "Clean" : "Mirror");

  const read = (): void => {
    for (const k of LAYERS) left[k] = base[k] ? Math.min(1, (startLayers[k] * sample(ctxs[k])) / base[k]) : 0;
    clean = 1 - (0.45 * left.rust + 0.3 * left.grime + 0.25 * left.haze);
  };

  const say = (msg: string, hold = false): void => {
    hintEl.textContent = msg;
    window.clearTimeout(sayTimer);
    if (hold) sayTimer = window.setTimeout(() => (hintEl.textContent = toolHint(holding)), 2200);
  };

  const suggest = (): void => {
    const want = needed();
    foam.querySelectorAll<HTMLElement>(".mb-slot").forEach((slot) => slot.classList.toggle("hint", slot.dataset.t === want && want !== holding));
  };

  const setTool = (id: ToolId, quiet = false): void => {
    if (id === holding && held.innerHTML) return;
    holding = id;
    held.innerHTML = TOOLSVG[id];
    guide.innerHTML = TOOLSVG[id];
    foam.querySelectorAll<HTMLElement>(".mb-slot").forEach((slot) => {
      const mine = slot.dataset.t === id;
      slot.classList.toggle("out", mine);
      slot.classList.toggle("live", mine);
      slot.setAttribute("aria-pressed", String(mine));
    });
    say(toolHint(id));
    suggest();
    if (!quiet) {
      held.classList.remove("swap");
      void held.offsetWidth;
      held.classList.add("swap");
    }
  };

  for (const id of TOOL_IDS) {
    const slot = document.createElement("button");
    slot.type = "button";
    slot.className = "mb-slot";
    slot.dataset.t = id;
    slot.setAttribute("aria-label", t("take the {tool}", { tool: toolName(id) }));
    slot.innerHTML = `<span class="mb-sil">${TOOLSVG[id]}</span><span class="mb-real">${TOOLSVG[id]}</span><span class="mb-nm"></span>`;
    slot.querySelector(".mb-nm")!.textContent = toolName(id);
    slot.addEventListener("click", () => setTool(id));
    foam.append(slot);
  }

  const paintState = (quiet = false): void => {
    const w = word();
    q(".mb-word").textContent = t(w);
    q(".mb-pct").textContent = done() ? t("holo") : `${Math.round(clean * 100)}%`;
    tag.className = `mb-tag${w === "Clean" || w === "Mirror" ? " good" : ""}${done() ? " holo-tag" : ""}`;
    medal.style.setProperty("--dull", String(dullOf(left)));
    if (w !== lastWord) {
      if (lastWord && !quiet) {
        ring();
        tag.classList.add("stamp");
        later(() => tag.classList.remove("stamp"), 500);
      }
      lastWord = w;
    }
    suggest();
  };

  // ---------- the held tool ----------
  const moveHeld = (x: number, y: number, touch: boolean): void => {
    const dx = x - lastHX;
    lastHX = x;
    held.style.transform = `translate(${x}px,${y - (touch ? 56 : 0)}px) rotate(${Math.max(-18, Math.min(18, dx * 1.2))}deg)`;
  };
  const onDocMove = (e: PointerEvent): void => {
    if (over || rubbing) moveHeld(e.clientX, e.clientY, e.pointerType === "touch");
  };
  const onDocUp = (e: PointerEvent): void => {
    held.classList.remove("press");
    if (e.pointerType === "touch") held.classList.remove("on");
  };
  baize.addEventListener("pointerenter", (e) => {
    if (e.pointerType === "touch") return;
    over = true;
    held.classList.add("on");
    baize.classList.add("holding");
    moveHeld(e.clientX, e.clientY, false);
  });
  baize.addEventListener("pointerleave", () => {
    tilt.style.setProperty("--rx", "0deg");
    tilt.style.setProperty("--ry", "0deg");
    if (rubbing) return;
    over = false;
    held.classList.remove("on", "press");
    baize.classList.remove("holding");
  });
  baize.addEventListener("pointerdown", (e) => {
    held.classList.add("on", "press");
    moveHeld(e.clientX, e.clientY, e.pointerType === "touch");
  });
  baize.addEventListener("pointermove", (e) => {
    if (!rubbing) tiltTo(e);
  });
  document.addEventListener("pointermove", onDocMove);
  document.addEventListener("pointerup", onDocUp);

  // ---------- rubbing ----------
  const pt = (e: PointerEvent): { x: number; y: number } => {
    const r = rust.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / (r.width || W)) * W, y: ((e.clientY - r.top) / (r.height || W)) * W };
  };
  const alphaAt = (ctx: CanvasRenderingContext2D, p: { x: number; y: number }): number =>
    ctx.getImageData(Math.max(0, Math.min(W - 1, p.x | 0)), Math.max(0, Math.min(W - 1, p.y | 0)), 1, 1).data[3];

  const retort = (above: LayerId): string => (above === "rust" ? t("That is rust. Scrape it off with the brush first.") : t("That is grime. Scrub it off with the sponge first."));

  const nope = (p: { x: number; y: number }, msg: string): void => {
    const now = performance.now();
    if (now - nopeAt < 700) return;
    nopeAt = now;
    held.classList.remove("nope");
    void held.offsetWidth;
    held.classList.add("nope");
    say(msg, true);
    emit(p, 4, "dust");
    if (!calm()) buzz([8, 30, 8]);
    kick();
  };

  const buzz = (pattern: number | number[]): void => {
    if (calm() || typeof navigator === "undefined" || !navigator.vibrate) return;
    try {
      navigator.vibrate(pattern);
    } catch {
      // not every device lets a page vibrate
    }
  };

  // one stroke of the tool from a to b. it only lifts its own layer, and only where the layers above it are already gone
  const scrub = (a: { x: number; y: number }, b: { x: number; y: number }): void => {
    const tool = TOOLS[holding];
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const own = ctxs[tool.layer];
    const hit = alphaAt(own, b) > 40;
    let blocked: LayerId | null = null;
    const steps = Math.max(1, Math.ceil(dist / 6));
    own.globalCompositeOperation = "destination-out";
    own.lineCap = own.lineJoin = "round";
    own.strokeStyle = `rgba(0,0,0,${tool.p})`;
    own.lineWidth = tool.w * (W / 270);
    for (let i = 1; i <= steps; i++) {
      const from = { x: a.x + ((b.x - a.x) * (i - 1)) / steps, y: a.y + ((b.y - a.y) * (i - 1)) / steps };
      const to = { x: a.x + ((b.x - a.x) * i) / steps, y: a.y + ((b.y - a.y) * i) / steps };
      const above: LayerId | null = tool.layer === "rust" ? null : alphaAt(ctxs.rust, to) > 40 ? "rust" : tool.layer === "haze" && alphaAt(ctxs.grime, to) > 40 ? "grime" : null;
      if (above) {
        blocked = above;
        continue;
      }
      own.beginPath();
      own.moveTo(from.x, from.y);
      own.lineTo(to.x, to.y);
      own.stroke();
    }
    if (dist <= 2) return;
    if (blocked) nope(b, retort(blocked));
    else if (left[tool.layer] < DONE && !done() && needed() !== holding) nope(b, t("Nothing left for the {tool}. Try the {next}.", { tool: toolName(holding), next: toolName(needed()) }));
    else rubFx(b, a, hit);
  };

  const afterRub = (): void => {
    read();
    paintState();
    if (done()) finish();
  };

  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    try {
      rust.setPointerCapture?.(e.pointerId);
    } catch {
      // the pointer may already be gone
    }
    rubbing = true;
    medal.classList.add("rubbing");
    guide.classList.add("gone");
    last = pt(e);
  };
  const onMove = (e: PointerEvent): void => {
    if (!rubbing) {
      tiltTo(e);
      return;
    }
    const p = pt(e);
    scrub(last, p);
    last = p;
    const now = performance.now();
    if (now - vibeAt > 90) {
      vibeAt = now;
      buzz(4);
      afterRub();
    }
    kick();
  };
  const onUp = (): void => {
    if (!rubbing) return;
    rubbing = false;
    medal.classList.remove("rubbing");
    afterRub();
    queueSave();
  };
  rust.addEventListener("pointerdown", onDown);
  rust.addEventListener("pointermove", onMove);
  rust.addEventListener("pointerup", onUp);
  rust.addEventListener("pointercancel", onUp);

  // keyboard: each press of space or enter sweeps three rows of the medal with the tool in hand
  rust.setAttribute("aria-label", t("Rub the medal with the {tool}. Press space to scrub.", { tool: toolName(holding) }));
  rust.addEventListener("keydown", (e) => {
    if (e.key !== " " && e.key !== "Enter") return;
    e.preventDefault();
    const tool = TOOLS[holding];
    const gap = tool.w * (W / 270) * 0.8;
    const rows = Math.max(1, Math.ceil(W / gap));
    const mid = Math.floor(rows / 2);
    for (let n = 0; n < 3; n++) {
      const k = keyRow++;
      const row = (((mid + Math.ceil(k / 2) * (k % 2 ? 1 : -1)) % rows) + rows) % rows;
      const y = row * gap + gap / 2;
      scrub({ x: 0, y }, { x: W, y });
    }
    afterRub();
    queueSave();
  });
  const refreshLabel = (): void => rust.setAttribute("aria-label", t("Rub the medal with the {tool}. Press space to scrub.", { tool: toolName(holding) }));
  foam.addEventListener("click", refreshLabel);

  // ---------- saving ----------
  const flushSave = (): void => {
    window.clearTimeout(saveTimer);
    saveTimer = 0;
    const dirt = dirtOfLayers(left);
    if (dirt < lastSaved - 0.001) {
      lastSaved = dirt;
      opts.onSave(dirt);
    }
  };
  function queueSave(): void {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(flushSave, 700);
  }

  // ---------- effects ----------
  function emit(p: { x: number; y: number }, n: number, kind: string): void {
    for (let i = 0; i < n; i++) {
      const q2: Particle = { kind, x: p.x, y: p.y, vx: (Math.random() - 0.5) * 3, vy: -Math.random() * 2, g: 0.18, life: 40 + Math.random() * 20, c: "#fff", s: 2 + Math.random() * 3 };
      if (kind === "flake") q2.c = ["#8c3c12", "#c8702e", "#5a2a10", "#d9964a"][Math.floor(Math.random() * 4)];
      else if (kind === "dust") Object.assign(q2, { vx: Math.random() - 0.5, vy: -0.3, g: 0, life: 34, s: 8 });
      else if (kind === "bubble") Object.assign(q2, { vx: (Math.random() - 0.5) * 0.8, vy: -0.5 - Math.random() * 0.6, g: -0.01, life: 56, s: 4 + Math.random() * 8 });
      else if (kind === "glow") Object.assign(q2, { vx: 0, vy: 0, g: 0, life: 12, s: 22 });
      else if (kind === "glint") Object.assign(q2, { vx: (Math.random() - 0.5) * 1.5, vy: (Math.random() - 0.5) * 1.5, g: 0, life: 26, s: 5 + Math.random() * 6 });
      parts.push(q2);
    }
  }
  // brush throws rust and dust, sponge makes suds, cloth draws bright shine streaks
  function rubFx(p: { x: number; y: number }, from: { x: number; y: number }, hit: boolean): void {
    if (holding === "brush") {
      if (hit) {
        emit(p, 3, "flake");
        if (Math.random() < 0.35) emit(p, 1, "dust");
      }
    } else if (holding === "sponge") {
      if (Math.random() < 0.7) emit(p, 1, "bubble");
      if (hit && Math.random() < 0.4) emit(p, 1, "bubble");
    } else {
      emit(p, 1, "glow");
      parts.push({ kind: "streak", x: from.x, y: from.y, x2: p.x, y2: p.y, vx: 0, vy: 0, g: 0, life: 14, c: "#fff", s: 5 });
      if (Math.random() < 0.6) emit(p, 1, "glint");
    }
  }
  function kick(): void {
    if (!loop && !closed) loop = requestAnimationFrame(tick);
  }
  function tick(): void {
    loop = 0;
    if (closed) return;
    fctx.clearRect(0, 0, W, W);
    parts = parts.filter((p) => p.life > 0);
    for (const q2 of parts) {
      q2.life--;
      const k = q2.kind;
      if (k === "streak") {
        fctx.globalCompositeOperation = "lighter";
        fctx.globalAlpha = q2.life / 14;
        fctx.strokeStyle = "#fff";
        fctx.lineWidth = 5;
        fctx.lineCap = "round";
        fctx.beginPath();
        fctx.moveTo(q2.x, q2.y);
        fctx.lineTo(q2.x2 ?? q2.x, q2.y2 ?? q2.y);
        fctx.stroke();
        fctx.globalCompositeOperation = "source-over";
        continue;
      }
      q2.x += q2.vx;
      q2.y += q2.vy;
      q2.vy += q2.g;
      if (k === "flake") {
        fctx.globalAlpha = Math.max(0, q2.life / 30);
        fctx.fillStyle = q2.c;
        fctx.fillRect(q2.x, q2.y, q2.s, q2.s);
      } else if (k === "glint") {
        fctx.globalAlpha = Math.max(0, Math.min(1, q2.life / 20));
        fctx.fillStyle = q2.c;
        fctx.fillRect(q2.x - q2.s / 2, q2.y - 0.75, q2.s, 1.5);
        fctx.fillRect(q2.x - 0.75, q2.y - q2.s / 2, 1.5, q2.s);
      } else if (k === "bubble") {
        fctx.globalAlpha = Math.max(0, q2.life / 40) * 0.8;
        fctx.strokeStyle = "#dff6ff";
        fctx.lineWidth = 1.5;
        fctx.beginPath();
        fctx.arc(q2.x, q2.y, q2.s, 0, 7);
        fctx.stroke();
        fctx.fillStyle = "rgba(255,255,255,.35)";
        fctx.beginPath();
        fctx.arc(q2.x - q2.s / 3, q2.y - q2.s / 3, q2.s / 4, 0, 7);
        fctx.fill();
      } else if (k === "dust") {
        const r = q2.s * (1 + (34 - q2.life) / 10);
        const g = fctx.createRadialGradient(q2.x, q2.y, 0, q2.x, q2.y, r);
        g.addColorStop(0, "rgba(150,110,80,.5)");
        g.addColorStop(1, "rgba(150,110,80,0)");
        fctx.globalAlpha = q2.life / 34;
        fctx.fillStyle = g;
        fctx.beginPath();
        fctx.arc(q2.x, q2.y, r, 0, 7);
        fctx.fill();
      } else if (k === "glow") {
        fctx.globalCompositeOperation = "lighter";
        const g2 = fctx.createRadialGradient(q2.x, q2.y, 0, q2.x, q2.y, q2.s);
        g2.addColorStop(0, "rgba(255,255,255,.75)");
        g2.addColorStop(1, "rgba(255,255,255,0)");
        fctx.globalAlpha = q2.life / 12;
        fctx.fillStyle = g2;
        fctx.beginPath();
        fctx.arc(q2.x, q2.y, q2.s, 0, 7);
        fctx.fill();
        fctx.globalCompositeOperation = "source-over";
      }
    }
    fctx.globalAlpha = 1;
    if (parts.length) loop = requestAnimationFrame(tick);
  }
  function ring(): void {
    if (calm()) return;
    const r = document.createElement("i");
    r.className = "mb-ring";
    baize.append(r);
    later(() => r.remove(), 900);
  }
  function finish(): void {
    if (medal.classList.contains("polished")) return;
    for (const k of LAYERS) ctxs[k].clearRect(0, 0, W, W);
    left.rust = left.grime = left.haze = 0;
    clean = 1;
    paintState();
    medal.classList.add("polished");
    const rays = document.createElement("i");
    rays.className = "mb-rays";
    const flash = document.createElement("i");
    flash.className = "mb-flash";
    const sweep = document.createElement("i");
    sweep.className = "mb-sweep";
    baize.append(rays, flash);
    medal.querySelector(".m-face")!.append(sweep);
    ring();
    baize.classList.add("shake");
    later(() => {
      rays.remove();
      flash.remove();
      sweep.remove();
      baize.classList.remove("shake");
    }, 2300);
    const colors = ["#ff2d95", "#ffe14d", "#3dffb0", "#35b8ff", "#8a4dff", "#ffffff"];
    const count = calm() ? 20 : 80;
    for (let i = 0; i < count; i++) {
      const a = Math.random() * 6.283;
      const v = 2 + Math.random() * 5;
      parts.push({ kind: "glint", x: W / 2, y: W / 2, vx: Math.cos(a) * v, vy: Math.sin(a) * v, g: 0.05, life: 40 + Math.random() * 30, c: colors[i % colors.length], s: 4 + Math.random() * 7 });
    }
    buzz([12, 40, 12, 40, 24]);
    say(t("fully polished"));
    flushSave();
    kick();
  }

  // ---------- tilt and holo follow the pointer, a touch device drifts by itself (css) ----------
  function tiltTo(e: PointerEvent): void {
    if (e.pointerType === "touch" || calm()) return;
    const r = baize.getBoundingClientRect();
    const x = (e.clientX - r.left) / (r.width || 1);
    const y = (e.clientY - r.top) / (r.height || 1);
    tilt.style.setProperty("--rx", `${((x - 0.5) * 26).toFixed(1)}deg`);
    tilt.style.setProperty("--ry", `${((0.5 - y) * 22).toFixed(1)}deg`);
    medal.style.setProperty("--mx", `${(x * 100).toFixed(0)}%`);
    medal.style.setProperty("--my", `${(y * 100).toFixed(0)}%`);
  }

  // ---------- open and close ----------
  const hide = (): void => {
    if (closed) return;
    flushSave();
    closed = true;
    cancelAnimationFrame(loop);
    for (const id of timers) window.clearTimeout(id);
    window.clearTimeout(sayTimer);
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("pointermove", onDocMove);
    document.removeEventListener("pointerup", onDocUp);
    document.body.classList.remove("mb-open");
    sheet.remove();
    opts.onClose();
    opts.returnFocus?.focus?.();
  };
  const release = pushLayer(hide);
  const close = (): void => {
    if (closed) return;
    hide();
    release();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  putBack.addEventListener("click", close);
  // a tap on the wood outside the bench closes it too
  sheet.addEventListener("click", (e) => {
    if (e.target === sheet) close();
  });

  document.body.classList.add("mb-open");
  document.body.append(sheet);
  setTool(polishedAtStart ? "cloth" : needed(), true);
  guide.classList.toggle("gone", polishedAtStart);
  held.classList.remove("swap");
  paintState(true);
  say(polishedAtStart ? t("fully polished") : toolHint(holding));
  rust.focus?.({ preventScroll: true });
  refreshLabel();
  return { close, root: sheet };
}
